import type {
  CheckinStatus,
  CheckinAuditEntry,
  EnrichedCheckin,
  EnrichedCheckinItem,
  SupplierBoxSummary,
} from "./types";
import type { UserRole } from "@/features/auth/currentUserTypes";
import { checkinCompletionPercentage } from "./completion";
import { buildReceivingSummary } from "./receivingViewModel";
import { isLegacyDirectKgItem } from "@/lib/quantity";

export const CHECKIN_STATUS_COLORS: Record<CheckinStatus, string> = {
  CONCLUIDO: "var(--login-muted)",
  CARREGAMENTO: "#2563eb",
  ENCERRADO: "#16a34a",
};

export const CHECKIN_STATUS_LABELS: Record<CheckinStatus, string> = {
  CONCLUIDO: "Não iniciado",
  CARREGAMENTO: "Carregamento",
  ENCERRADO: "Encerrado",
};

const CHECKIN_AUDIT_OPERATION_LABELS: Record<string, string> = {
  ADD_RECEIPT: "Recebimento registrado",
  UNDO_RECEIVING_ACTION: "Recebimento desfeito",
  CLOSE_SHORTFALL: "Saldo pendente encerrado",
  REOPEN_SHORTFALL: "Saldo pendente reaberto",
  REQUEST_RETURN: "Devolução solicitada",
  DECIDE_RETURN: "Decisão de devolução registrada",
  REOPEN_RETURN_DECISION: "Decisão de devolução reaberta",
  UNDO_RETURN_ACTION: "Devolução desfeita",
  CHECK_ITEM: "Conferência registrada",
  QUICK_CHECK_ITEM: "Conferência rápida registrada",
  UNDO_CHECK_ITEM: "Conferência desfeita",
  TOGGLE_STATUS_DONE: "Carga fechada",
  TOGGLE_STATUS_REOPENED: "Carga reaberta",
  UNDO_LIFECYCLE: "Alteração da carga desfeita",
};

export function formatCheckinAuditOperation(operation: string) {
  return CHECKIN_AUDIT_OPERATION_LABELS[operation] ?? operation;
}

export function getCheckinAuditOperation(event: CheckinAuditEntry) {
  const fallback = event.payload._fallback_operation;
  return typeof fallback === "string" ? fallback : event.operation;
}

export function buildCheckinPageDisplay(
  checkin: EnrichedCheckin,
  {
    isClientView,
    isSupplierView,
    canManageCheckins,
    canAudit,
    role,
  }: {
    isClientView: boolean;
    isSupplierView: boolean;
    canManageCheckins: boolean;
    canAudit: boolean;
    role: UserRole;
  },
) {
  const statusColor =
    CHECKIN_STATUS_COLORS[checkin.status] ?? "var(--login-muted)";
  const hasLegacyDirectKgItem = Boolean(
    checkin.items?.some(isLegacyDirectKgItem),
  );
  const isReadOnly =
    checkin.status === "ENCERRADO" ||
    !canManageCheckins ||
    hasLegacyDirectKgItem;
  const canManageLifecycle = !hasLegacyDirectKgItem && canManageCheckins;
  const progressPct = checkinCompletionPercentage(
    checkin.checked_items,
    checkin.total_items,
  );

  return {
    statusLabel: CHECKIN_STATUS_LABELS[checkin.status],
    statusColor,
    isSupplierView,
    hasLegacyDirectKgItem,
    isReadOnly,
    canManageLifecycle,
    canManageItems: canManageCheckins,
    canManageBoxes: role === "super" || role === "checker",
    canViewBoxes:
      !isClientView && (canManageCheckins || canAudit || role === "supplier"),
    canCreateAlert: role === "super" || role === "checker",
    progressPct,
  };
}

export function formatCheckinUpdateTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Atualização indisponível";
  return `Atualizada ${new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "America/Sao_Paulo",
  }).format(date)}`;
}

export function filterItemAuditEvents(
  events: CheckinAuditEntry[],
  itemId: string,
) {
  return events
    .filter((event) => event.payload.checkin_item_id === itemId)
    .sort((left, right) => {
      const changedAt =
        new Date(right.changed_at).getTime() -
        new Date(left.changed_at).getTime();
      return changedAt || right.id - left.id;
    });
}

export function checkHistoryUndoReason(
  event: CheckinAuditEntry,
  events: CheckinAuditEntry[],
  item: EnrichedCheckinItem,
  canWrite: boolean,
  closed: boolean,
): string | null {
  if (!canWrite) return "Apenas conferentes e supervisores podem desfazer.";
  if (closed) return "Reabra a carga para desfazer.";
  if (filterItemAuditEvents(events, item.id)[0]?.id !== event.id)
    return "Há uma ação posterior neste item.";
  if (
    event.payload.new_status !== item.status ||
    event.payload.new_received_quantity !== item.received_quantity ||
    event.payload.new_checked_at !== item.checked_at ||
    event.payload.new_checked_by !== item.checked_by
  )
    return "O estado do item foi alterado. Atualize o histórico.";
  return null;
}

export function filterLifecycleAuditEvents(events: CheckinAuditEntry[]) {
  return events
    .filter((event) =>
      ["TOGGLE_STATUS_DONE", "TOGGLE_STATUS_REOPENED"].includes(
        getCheckinAuditOperation(event),
      ),
    )
    .sort((a, b) => b.id - a.id);
}

export function lifecycleHistoryUndoReason(
  event: CheckinAuditEntry,
  events: CheckinAuditEntry[],
  status: CheckinStatus,
  canWrite: boolean,
): string | null {
  if (!canWrite) return "Apenas conferentes e supervisores podem desfazer.";
  if (events.some((entry) => entry.id > event.id))
    return "Há uma ação posterior na carga.";
  const operation = getCheckinAuditOperation(event);
  if (
    (operation === "TOGGLE_STATUS_DONE" && status !== "ENCERRADO") ||
    (operation === "TOGGLE_STATUS_REOPENED" && status !== "CARREGAMENTO")
  )
    return "O estado da carga mudou. Atualize o histórico.";
  if (typeof event.payload.state_snapshot !== "string")
    return "Evento histórico antigo sem estado verificável; use Fechar ou Reabrir.";
  return null;
}

export function canUndoReceivingHistoryEvent(
  event: CheckinAuditEntry,
  events: CheckinAuditEntry[],
  item: EnrichedCheckinItem,
  canWrite: boolean,
) {
  return Boolean(
    canWrite &&
    receivingHistorySource(event, item) &&
    !receivingHistoryUndoReason(event, item, canWrite, false, events),
  );
}

export function receivingHistorySource(
  event: CheckinAuditEntry,
  item: EnrichedCheckinItem,
) {
  const operation = getCheckinAuditOperation(event);
  if (
    event.payload.checkin_item_id !== item.id ||
    typeof event.payload.receipt_id !== "string"
  )
    return null;
  const receipt = (
    item.receipts ?? (item.last_receipt ? [item.last_receipt] : [])
  ).find(
    (entry) =>
      entry.id === event.payload.receipt_id &&
      entry.checkin_item_id === item.id,
  );
  return receipt &&
    ((operation === "ADD_RECEIPT" && receipt.kind === "RECEIPT") ||
      (operation === "CLOSE_SHORTFALL" && receipt.kind === "SHORTFALL_CLOSE"))
    ? receipt
    : null;
}

export function receivingHistoryUndoReason(
  event: CheckinAuditEntry,
  item: EnrichedCheckinItem,
  canWrite: boolean,
  closed: boolean,
  events: CheckinAuditEntry[] = [],
) {
  const source = receivingHistorySource(event, item);
  if (!canWrite || !source) return null;
  if (closed) return "Reabra a carga para desfazer.";
  if (
    typeof event.payload.expected_quantity === "number" &&
    Math.abs(event.payload.expected_quantity - item.expected_quantity) > 0.0001
  )
    return "A quantidade esperada mudou desde esta ação.";
  if (
    events.some((later) => {
      if (later.id <= event.id || later.payload.checkin_item_id !== item.id)
        return false;
      const operation = getCheckinAuditOperation(later);
      return (
        operation !== "ADD_RECEIPT" &&
        !(
          operation === "UNDO_RECEIVING_ACTION" &&
          later.payload.source_kind === "RECEIPT" &&
          later.payload.source_receipt_id !== source.id
        )
      );
    })
  )
    return "Há uma ação posterior dependente neste item.";
  if (
    item.receipts?.some(
      (receipt) =>
        receipt.kind === "REVERSAL" &&
        receipt.reverses_receipt_id === source.id,
    )
  )
    return "Recebimento já desfeito.";
  if (item.return_lots?.some((lot) => lot.status !== "NONE"))
    return "Há devolução ativa neste item.";
  if (source.kind === "RECEIPT" && item.is_shortfall_closed)
    return "Reabra o saldo pendente antes de desfazer.";
  return null;
}

export function canUndoReturnHistoryEvent(
  event: CheckinAuditEntry,
  events: CheckinAuditEntry[],
  item: EnrichedCheckinItem,
  canWrite: boolean,
  closed: boolean,
) {
  if (!canWrite || closed || event.payload.checkin_item_id !== item.id)
    return false;
  const latest = filterItemAuditEvents(events, item.id)[0];
  if (!latest || latest.id !== event.id) return false;
  const lot = item.return_lots?.find(
    (entry) => entry.id === event.payload.return_lot_id,
  );
  if (!lot) return false;
  if (
    events.some(
      (entry) =>
        entry.id > event.id &&
        (entry.payload.return_lot_id === lot.id ||
          entry.payload.checkin_item_id === item.id),
    )
  )
    return false;
  if (
    lot.replacement_checkin_item_id &&
    events.some(
      (entry) =>
        entry.id > event.id &&
        entry.payload.checkin_item_id === lot.replacement_checkin_item_id,
    )
  )
    return false;
  const previous = event.payload.previous;
  if (!previous || typeof previous !== "object" || !("status" in previous))
    return false;
  const operation = getCheckinAuditOperation(event);
  return (
    (operation === "REQUEST_RETURN" &&
      typeof previous.status === "string" &&
      lot.status === "DECISION" &&
      item.return_status === "DECISION") ||
    (operation === "DECIDE_RETURN" &&
      previous.status === "DECISION" &&
      (lot.status === "SUBSTITUIDO" || lot.status === "DEVOLVIDO") &&
      lot.status === item.return_status)
  );
}

export function filterCheckinItems(
  items: EnrichedCheckinItem[],
  searchTerm: string,
) {
  if (!searchTerm.trim()) return sortCheckinItems(items);
  const term = searchTerm.toLowerCase();
  return sortCheckinItems(
    items.filter(
      (item) =>
        item.product_name.toLowerCase().includes(term) ||
        item.supplier_name.toLowerCase().includes(term),
    ),
  );
}

function getCheckinItemSortBucket(item: EnrichedCheckinItem) {
  const receiving = buildReceivingSummary(item);
  const returned = item.returned_quantity ?? 0;

  const hasPendingReturnDecision =
    item.status === "DECISION" ||
    item.return_status === "DECISION" ||
    item.return_lots?.some((lot) => lot.status === "DECISION");

  if (hasPendingReturnDecision) return 0;
  if (
    item.status === "UNAVAILABLE" ||
    item.status === "DEVOLVIDO" ||
    (receiving.expectedQuantity <= 0 && returned > 0)
  ) {
    return 2;
  }
  if (receiving.isShortfallClosed) return 3;
  if (receiving.status === "SURPLUS") return 1;
  if (receiving.status === "COMPLETE") return 4;
  return 0;
}

export function sortCheckinItems(items: EnrichedCheckinItem[]) {
  const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

  return [...items].sort((left, right) => {
    const bucketDiff =
      getCheckinItemSortBucket(left) - getCheckinItemSortBucket(right);
    if (bucketDiff !== 0) return bucketDiff;

    const productDiff = collator.compare(left.product_name, right.product_name);
    if (productDiff !== 0) return productDiff;

    const supplierDiff = collator.compare(
      left.supplier_name,
      right.supplier_name,
    );
    if (supplierDiff !== 0) return supplierDiff;

    return collator.compare(left.id, right.id);
  });
}

export function filterSupplierBoxSummaries(
  suppliers: SupplierBoxSummary[],
  searchTerm: string,
) {
  if (!searchTerm.trim()) return suppliers;
  const term = searchTerm.toLowerCase();
  return suppliers.filter((supplier) =>
    supplier.supplier_name.toLowerCase().includes(term),
  );
}
